/**
 * Organization Admin Access Control & Contact Helpers
 *
 * Rules:
 * - A user is an Organization Admin if:
 *   1. user.isOwner === true or user.organizationRole?.isOwner === true
 *   2. OR their role code contains 'ADMIN' (e.g., ROLE_SUBSCRIBER_ADMIN, ROLE_HOST_ADMIN, ROLE_ADMIN, etc.)
 *   3. OR user.organizationRole?.code === 'ROLE_SUBSCRIBER_OWNER' or 'ROLE_HOST_CUSTOMER_OWNER'
 *   EXCEPT:
 *   - Platform Superadmin (ROLE_ADMINISTRATOR, orgTypeId 1, PLATFORM_ADMIN) is excluded from org-level subscription checkout.
 *
 * Non-admins (employees, approvers, operations) are not permitted to initiate or execute subscription/package purchases.
 */

export const isOrgAdmin = (user) => {
  if (!user) return false;

  const roleCode = (
    user.organizationRole?.code ||
    user.role ||
    user.roleCode ||
    ""
  ).toUpperCase();

  // Exclude Platform Superadmin
  if (
    roleCode === "ROLE_ADMINISTRATOR" ||
    user?.organization?.type?.code === "PLATFORM_ADMIN" ||
    user?.organization?.typeId === 1
  ) {
    return false;
  }

  // Check if explicit owner flag is true
  const isOwner = Boolean(user.isOwner || user.organizationRole?.isOwner);
  if (isOwner) return true;

  // Check if role code includes ADMIN (e.g. ROLE_ORG_ADMIN, etc.)
  if (roleCode.includes("ADMIN")) return true;

  // Known organization owner/admin role codes in Spacia
  const knownAdminRoles = [
    "ROLE_SUBSCRIBER_OWNER",
    "ROLE_HOST",
    "ROLE_SUBSCRIBER_HOST",
    "ROLE_HOST_CUSTOMER_OWNER",
  ];

  return knownAdminRoles.includes(roleCode);
};

/**
 * Extract organization contact email from user object
 */
export const getOrgContactEmail = (user) => {
  if (!user) return null;
  return (
    user.organization?.contactEmail ||
    user.organization?.email ||
    user.organization?.adminProfile?.emailAddress ||
    user.companyContactEmail ||
    null
  );
};

/**
 * Extract organization name from user object
 */
export const getOrgName = (user) => {
  if (!user) return "your organization";
  return user.organization?.name || user.companyName || "your organization";
};
